/**
 * @file Login.tsx
 *
 * Gravity-styled admin sign-in screen for the password auth flow (local and
 * preview environments). Submits username/password — plus an optional
 * `wsbV2projectUuid` for local/preview project parity — to `POST /auth/login`,
 * which sets the session cookie. On success it notifies the caller so the
 * {@link AuthProvider} can transition the app to its authenticated state. Token
 * mode never renders this screen (it exchanges a short-lived token instead).
 */

import { useState, type FormEvent } from 'react';
import { Button, GeneralField, Notice, Password } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { apiFetch } from '../api/client';
import { adminMessages } from '../admin/i18n/messages';
import { DEFAULT_PROJECT_UUID } from '../config';
import styles from './Login.module.css';

interface LoginProps {
  /** Invoked once the login request succeeds and the session cookie is set. */
  onAuthenticated: () => void;
}

interface LoginPayload {
  username: string;
  password: string;
  wsbV2projectUuid?: string;
}

/**
 * Renders the password-mode sign-in form.
 *
 * @param props.onAuthenticated - Called after a successful login.
 *
 * @returns The login screen.
 */
export function Login({ onAuthenticated }: LoginProps) {
  const intl = useIntl();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [projectUuid, setProjectUuid] = useState(DEFAULT_PROJECT_UUID);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setFailed(false);
    setSubmitting(true);

    const payload: LoginPayload = { username, password };
    const uuid = projectUuid.trim();
    if (uuid) {
      payload.wsbV2projectUuid = uuid;
    }

    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        onAuthenticated();
        return;
      }
      setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <main className={`gv-activated ${styles.card}`}>
        <div className={styles.heading}>
          <h1 className={styles.title}>
            {intl.formatMessage(adminMessages.loginTitle)}
          </h1>
          <p className={styles.subtitle}>
            {intl.formatMessage(adminMessages.loginSubtitle)}
          </p>
        </div>

        {failed && (
          <Notice type="alert">
            {intl.formatMessage(adminMessages.loginError)}
          </Notice>
        )}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label className="gv-label" htmlFor="login-username">
              {intl.formatMessage(adminMessages.loginUsername)}
            </label>
            <GeneralField
              id="login-username"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <Password
            id="login-password"
            name="password"
            label={intl.formatMessage(adminMessages.loginPassword)}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <div className={styles.field}>
            <label className="gv-label" htmlFor="login-project-uuid">
              {intl.formatMessage(adminMessages.loginProjectUuid)}
            </label>
            <GeneralField
              id="login-project-uuid"
              name="wsbV2projectUuid"
              autoComplete="off"
              spellCheck={false}
              value={projectUuid}
              onChange={(e) => setProjectUuid(e.target.value)}
            />
          </div>

          <Button type="submit" variant="primary" disabled={submitting}>
            {intl.formatMessage(adminMessages.loginSubmit)}
          </Button>
        </form>
      </main>
    </div>
  );
}
